import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Tooltip from '#components/Elements/Tooltip/Tooltip';

const { ceil, floor } = Math;

export function seconds(count) {
  return count * 1000;
}

export function minutes(count) {
  return seconds(count * 60);
}

export function hours(count) {
  return minutes(count * 60);
}

function averageDays(count) {
  return hours(count * 24);
}

function averageWeeks(count) {
  return averageDays(count * 7);
}

function averageMonths(count) {
  return averageDays(count * 30.347);
}

function averageYears(count) {
  return averageDays(count * 365.2425);
}

/**
 * @param {Date} date1
 * @param {Date} date2
 */
function sameDay(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * @param {Date} date1
 * @param {Date} date2
 */
function sameYear(date1, date2) {
  return date1.getFullYear() === date2.getFullYear();
}

/**
 * @param {Date} date
 * @param {number} count
 */
function rollDays(date, count) {
  let rolled = new Date(date.getTime());
  rolled.setDate(date.getDate() + count);
  return rolled;
}

/**
 * @param {Date} date
 */
function truncateDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function formatRelative(intl, timestamp, style) {
  if (
    timestamp === undefined ||
    timestamp === null ||
    timestamp === false ||
    timestamp === true
  ) {
    return [null, ''];
  }
  let target;
  try {
    target = new Date(timestamp);
  } catch (error) {
    return [null, ''];
  }
  if (isNaN(target.getTime())) {
    return [null, ''];
  }

  const now = window.testSyntheticTime
    ? new Date(window.testSyntheticTime)
    : new Date();
  const age = now.getTime() - target.getTime();
  const untilTomorrow = truncateDay(rollDays(now, 1)).getTime() - now.getTime();
  const yesterday = rollDays(now, -1);
  const sevenDaysAgo = truncateDay(rollDays(now, -1));

  switch (style) {
    case 'timeOrDate':
    default:
      // In future: fall back to dateStyle: 'medium'
      if (age < 0) {
        break;
      }
      // < 12 hr ago or same day: “9:41 AM”
      if (age < hours(12) || sameDay(target, now)) {
        const ageMinutes = age / minutes(1);
        return [
          ceil(ageMinutes) * minutes(1) - age,
          intl.formatDate(target, {
            timeStyle: 'short',
          }),
        ];
      }
      // some time yesterday: “Yesterday”
      if (sameDay(target, yesterday)) {
        return [
          untilTomorrow,
          intl.formatMessage({ defaultMessage: 'Yesterday' }),
        ];
      }
      // < 7 days ago: "Monday", "Tuesday", etc
      if (target.getTime() > sevenDaysAgo.getTime()) {
        return [
          untilTomorrow, // Could wait for 7 day window, but this’ll do
          intl.formatDate(target, { weekday: 'long' }),
        ];
      }
      // >= 7 days ago, current year: Sep 7
      if (sameYear(target, now)) {
        return [
          untilTomorrow, // Could wait until next year, but this’ll do
          intl.formatDate(target, { month: 'short', day: 'numeric' }),
        ];
      }
      break;

    case 'hybrid':
      // In future: fall back to dateStyle: 'medium'
      if (age < 0) {
        break;
      }
      // < 1 min ago: “just now”
      if (age < minutes(1)) {
        return [
          minutes(1) - age,
          intl.formatMessage({ defaultMessage: 'Just now' }),
        ];
      }
      // < 1 hr ago: “15 minutes ago”
      if (age < hours(1)) {
        let ageMinutes = age / minutes(1);
        return [
          ceil(ageMinutes) * minutes(1) - age,
          intl.formatRelativeTime(-floor(ageMinutes), 'minutes'),
        ];
      }
      // < 12 hr ago or same day: “9:41 AM”
      if (age < hours(12) || sameDay(target, now)) {
        const ageMinutes = age / minutes(1);
        return [
          ceil(ageMinutes) * minutes(1) - age,
          intl.formatDate(target, {
            timeStyle: 'short',
          }),
        ];
      }
      // some time yesterday: “Yesterday”
      if (sameDay(target, yesterday)) {
        return [
          untilTomorrow,
          intl.formatMessage({ defaultMessage: 'Yesterday' }),
        ];
      }
      // < 7 days ago: "Monday", "Tuesday", etc
      if (target.getTime() > sevenDaysAgo.getTime()) {
        return [
          untilTomorrow, // Could wait for 7 day window, but this’ll do
          intl.formatDate(target, { weekday: 'long' }),
        ];
      }
      // >= 7 days ago, current year: Sep 7
      if (sameYear(target, now)) {
        return [
          untilTomorrow, // Could wait until next year, but this’ll do
          intl.formatDate(target, { month: 'short', day: 'numeric' }),
        ];
      }
      break;

    case 'dateOnly':
      // “today”
      if (sameDay(target, now)) {
        return [untilTomorrow, intl.formatMessage({ defaultMessage: 'Today' })];
      }
      // “yesterday”
      if (sameDay(target, yesterday)) {
        return [
          untilTomorrow,
          intl.formatMessage({ defaultMessage: 'Yesterday' }),
        ];
      }
      break;

    case 'ageOnly': {
      // < 1 min ago: “just now”
      if (age < minutes(1)) {
        return [
          minutes(1) - age,
          intl.formatMessage({ defaultMessage: 'Just now' }),
        ];
      }
      // < 1 hr ago: “15 minutes ago”
      if (age < hours(1)) {
        let ageMinutes = age / minutes(1);
        return [
          ceil(ageMinutes) * minutes(1) - age,
          intl.formatRelativeTime(-floor(ageMinutes), 'minutes'),
        ];
      }
      // < 1 day ago: “2 hours ago”
      if (age < hours(24)) {
        let ageHours = age / hours(1);
        return [
          ceil(ageHours) * hours(1) - age,
          intl.formatRelativeTime(-floor(ageHours), 'hours'),
        ];
      }
      // < 1 week ago: “3 days ago”
      if (age < averageWeeks(1)) {
        let ageDays = age / averageDays(1);
        return [
          ceil(ageDays) * averageDays(1) - age,
          intl.formatRelativeTime(-floor(ageDays), 'days'),
        ];
      }
      // < 1 month ago: “2 weeks ago”
      if (age < averageMonths(1)) {
        let ageWeeks = age / averageWeeks(1);
        return [
          ceil(ageWeeks) * averageWeeks(1) - age,
          intl.formatRelativeTime(-floor(ageWeeks), 'weeks'),
        ];
      }
      // < 1 year ago: “7 months ago”
      if (age < averageYears(1)) {
        let ageMonths = age / averageMonths(1);
        return [
          ceil(ageMonths) * averageMonths(1) - age,
          intl.formatRelativeTime(-floor(ageMonths), 'months'),
        ];
      }
      let ageYears = age / averageYears(1);
      return [
        ceil(ageYears) * averageYears(1) - age,
        intl.formatRelativeTime(-floor(ageYears), 'years'),
      ];
    }
  }

  return [null, intl.formatDate(target, { dateStyle: 'medium' })];
}

function DefaultRender({
  className,
  formattedString,
  moreDetailString,
  timestamp,
  test,
}) {
  return (
    <Tooltip text={moreDetailString} noWrap>
      <span className={className} data-test={test} data-timestamp={timestamp}>
        {formattedString}
      </span>
    </Tooltip>
  );
}

function RelativeTime({
  timestamp: originalTimestamp,
  style = 'timeOrDate',
  className,
  test,
  render = DefaultRender,
}) {
  const timestamp = window.testTransformTime
    ? window.testTransformTime(originalTimestamp)
    : originalTimestamp;

  const intl = useIntl();
  const [, recompute] = useState();
  const [recomputeAfter, formattedRelative] = formatRelative(
    intl,
    timestamp,
    style,
  );

  useEffect(() => {
    if (!recomputeAfter) return;

    const timeoutId = setTimeout(() => recompute({}), recomputeAfter);

    return () => clearTimeout(timeoutId);
  }, [recomputeAfter]);

  // Check for invalid timestamps
  if (
    timestamp === undefined ||
    timestamp === null ||
    timestamp === false ||
    timestamp === true
  ) {
    return null;
  }
  let timestampMilliseconds;
  try {
    timestampMilliseconds = new Date(timestamp).getTime();
  } catch (error) {
    return null;
  }
  if (isNaN(timestampMilliseconds)) {
    return null;
  }

  if (style === 'dateOnly' || style === 'ageOnly') {
    return render({ formattedString: formattedRelative, className });
  }

  const formattedAbsolute =
    formattedRelative &&
    intl.formatDate(timestamp, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });

  return render({
    timestamp: timestampMilliseconds,
    formattedString: formattedRelative,
    moreDetailString: formattedAbsolute,
    className,
    test,
  });
}

export default RelativeTime;
